<template>
  <article
    class="watch-card"
    :class="{ pressed, pulse: pulsing }"
    :style="cardStyle"
    @pointerdown="onDown"
    @pointerup="onUp"
    @pointerleave="onCancel"
    @pointercancel="onCancel"
    @contextmenu.prevent="openSheet"
  >
    <div class="head">
      <span class="fluid">{{ fluidLabel }}</span>
      <span v-if="meta" class="pill" :style="{ color: meta.color, borderColor: meta.color }">{{ meta.pill }}</span>
    </div>
    <FracTankVisual
      :name="tank.name"
      :capacity-bbl="cuts.capacityBbl"
      :spill-inches="cuts.spillInches"
      :oil-inches="cuts.oilInches"
      :water-inches="cuts.waterInches"
      :fill-token="fillToken"
      :width-px="kind.style === 'upright' ? 240 : 220"
      :tank-style="kind.style"
    />
    <div class="readout">
      <strong>{{ totalBbl.toFixed(1) }} BBL</strong>
      <p>Oil {{ oilBbl.toFixed(1) }} · Water {{ waterBbl.toFixed(1) }} · {{ pct }}%</p>
    </div>
  </article>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, ref } from 'vue'
import FracTankVisual from './FracTankVisual.vue'
import { fillRatio, isGettingHigh, normalizeWatchStatus, tankCuts, tankKind, watchMeta, type WatchStatus } from './fracTank'

const props = defineProps<{
  tank: any
  status?: WatchStatus
}>()

const emit = defineEmits<{
  tap: []
  longpress: []
}>()

const pressed = ref(false)
let hold: number | null = null
let longFired = false

const cuts = computed(() => tankCuts(props.tank))
const kind = computed(() => tankKind(props.tank.tankType || props.tank.tank_type))
const status = computed(() => props.status ?? normalizeWatchStatus(props.tank.watchStatus ?? props.tank.watch_status ?? props.tank.flag))
const meta = computed(() => watchMeta(status.value))
const pulsing = computed(() => isGettingHigh(props.tank, status.value))
const fillToken = computed(() => props.tank.measuredAt || props.tank.reading_at || props.tank.current?.measured_at || '')
const fluidLabel = computed(() => props.tank.fluidLabel || props.tank.fluid_label || props.tank.fluid_type || 'Production')
const oilBbl = computed(() => Number(props.tank.oilBbl ?? props.tank.oil_bbl ?? props.tank.current?.oil_bbl ?? 0))
const waterBbl = computed(() => Number(props.tank.waterBbl ?? props.tank.water_bbl ?? props.tank.current?.water_bbl ?? 0))
const totalBbl = computed(() => Number(props.tank.totalBbl ?? props.tank.total_bbl ?? props.tank.current?.total_bbl ?? (oilBbl.value + waterBbl.value)))
const pct = computed(() => Math.round(fillRatio(props.tank) * 100))
const color = computed(() => {
  if (meta.value) return meta.value.color
  if (pulsing.value) return '#FF6F7D'
  return '#2D4259'
})
const cardStyle = computed(() => ({
  borderColor: color.value,
  borderWidth: meta.value || pulsing.value ? '2.4px' : '1px',
  boxShadow: `0 0 18px ${hexAlpha(color.value, pulsing.value ? 0.22 : meta.value ? 0.22 : 0)}`,
}))

function hexAlpha(hex: string, a: number) {
  const n = hex.replace('#', '')
  const r = parseInt(n.slice(0, 2), 16)
  const g = parseInt(n.slice(2, 4), 16)
  const b = parseInt(n.slice(4, 6), 16)
  return `rgba(${r},${g},${b},${a})`
}

function onDown() {
  pressed.value = true
  longFired = false
  hold = window.setTimeout(() => {
    longFired = true
    emit('longpress')
  }, 400)
}

function onUp() {
  pressed.value = false
  if (hold) window.clearTimeout(hold)
  hold = null
  if (!longFired) emit('tap')
}

function onCancel() {
  pressed.value = false
  if (hold) window.clearTimeout(hold)
  hold = null
}

function openSheet() {
  emit('longpress')
}

onBeforeUnmount(() => {
  if (hold) window.clearTimeout(hold)
})
</script>

<style scoped>
.watch-card {
  width: 252px;
  flex: 0 0 252px;
  background: #17263A;
  border: 1px solid #2D4259;
  border-radius: 16px;
  padding: 10px 8px 12px;
  color: #EAF2F8;
  cursor: pointer;
  transition: transform 120ms ease, box-shadow 900ms ease;
}
.watch-card.pressed { transform: scale(0.96); }
.watch-card.pulse { animation: glow 900ms ease-in-out infinite alternate; }
.head { display: flex; justify-content: space-between; align-items: center; gap: 8px; margin-bottom: 4px; padding: 0 4px; }
.fluid { font-size: 11px; font-weight: 800; color: #92A5B7; text-transform: uppercase; letter-spacing: .04em; }
.pill {
  font-size: 10px; font-weight: 900; letter-spacing: .04em;
  border: 1px solid; border-radius: 999px; padding: 2px 7px;
}
.readout { text-align: center; margin-top: 4px; }
.readout strong { display: block; font-size: 22px; font-weight: 900; }
.readout p { margin: 2px 0 0; color: #92A5B7; font-size: 11px; }
@keyframes glow {
  from { filter: drop-shadow(0 0 8px rgba(255,111,125,.12)); }
  to { filter: drop-shadow(0 0 18px rgba(255,111,125,.35)); }
}
@media (prefers-reduced-motion: reduce) {
  .watch-card.pulse { animation: none; }
}
</style>
